<template>
    <BaseModal
        :title="i18n('modal.offerRoomActivation.title')"
        class="offerroomactivation"
        size="modal-md"
        :closable="true"
        :disposeAfterHide="true"
        ref="baseModalComponent"
        @hide="onHidden"
    >
        <template v-slot:body>
            <div class="name">{{ offer.name }}</div>
            <div class="grid">
                <RoomWithServiceListItem
                    v-for="room in rooms"
                    :key="room.id"
                    :room="room"
                    :offer="offer"
                    @offerUpdated="onOfferUpdated"
                />
            </div>
        </template>
        <template v-slot:footer>
            <button
                type="button"
                class="btn btn-clean btn-text-danger"
                :disabled="isResetBtnDisabled"
                @click="resetActivations"
            >
                {{ i18n("modal.offerRoomActivation.reset") }}
            </button>
        </template>
    </BaseModal>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { CatalogDao } from "@/data/dao/service/CatalogDao";
    import { RoomDao } from "@/data/dao/service/RoomDao";
    import type { IOfferWithScheduleState } from "@/data/model/Offer";
    import type { IRoomWithService } from "@/data/model/Room";
    import { i18n } from "@/portal/Trans";
    import { UiNotification } from "@/portal/ui/Notification";
    import { logError } from "@/shared/logging/errors";
    import { onMounted, ref } from "vue";

    import BaseModal from "@/components/dialog/BaseModal.vue";
    import RoomWithServiceListItem from "@/portal/components/modal/offerRoomActivation/RoomWithServiceListItem.vue";

    const catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);
    const roomDao = Factory.getInstance<RoomDao>(RoomDao);

    const props = defineProps<{
        offerWithScheduleState: IOfferWithScheduleState;
        onChanged?: () => void;
        onHidden?: () => void;
    }>();

    const offer = ref<IOfferWithScheduleState>(props.offerWithScheduleState);
    const rooms = ref<IRoomWithService[]>([]);
    const isResetBtnDisabled = ref(false);

    // define html refs
    const baseModalComponent = ref<InstanceType<typeof BaseModal> | null>(null);

    async function resetActivations(): Promise<void> {
        try {
            isResetBtnDisabled.value = true;
            const newOffer = await catalogDao.resetOfferActivations(offer.value.id);
            UiNotification.showSuccess(i18n("modal.offerRoomActivation.sucessfulReset"));
            onOfferUpdated(newOffer);
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("modal.offerRoomActivation.failedReset"));
        } finally {
            isResetBtnDisabled.value = false;
        }
    }

    async function loadRooms() {
        try {
            const roomsData = await roomDao.getData({ limit: 100 });
            rooms.value = roomsData.items;
        } catch (ex) {
            logError(ex);
            rooms.value = [];
        }
    }

    function onOfferUpdated(updatedOffer: IOfferWithScheduleState) {
        offer.value = updatedOffer;

        if (props.onChanged) {
            props.onChanged();
        }
    }

    onMounted(loadRooms);

    // define exposed
    function show() {
        baseModalComponent.value?.show();
    }

    function close() {
        baseModalComponent.value?.close();
    }

    defineExpose({
        show,
        close,
    });
</script>

<style lang="scss">
    .modal.offerroomactivation {
        .modal-dialog > .modal-content {
            > .modal-body {
                color: #595d6e;

                > .name {
                    font-size: 16px;
                    font-weight: 500;
                }

                > .grid {
                    padding-top: 16px;

                    > .row {
                        padding-top: 2px;
                        padding-bottom: 2px;
                        font-weight: 400;
                    }
                }
            }
        }
    }
</style>
